const MAX_CONCURRENT_STORAGE_KEY = 'validation.maxConcurrent';
const BATCH_SIZE_STORAGE_KEY = 'validation.batchSize';
const LAUNCH_INTERVAL_MS_STORAGE_KEY = 'validation.launchIntervalMs';

export const VALIDATION_MAX_CONCURRENT_DEFAULT = 1;
export const VALIDATION_MAX_CONCURRENT_MIN = 1;
export const VALIDATION_MAX_CONCURRENT_MAX = 10;

export const VALIDATION_BATCH_SIZE_DEFAULT = 10000;
export const VALIDATION_BATCH_SIZE_OPTIONS = [1000, 5000, 10000] as const;

export const VALIDATION_LAUNCH_INTERVAL_MS_DEFAULT = 0;
export const VALIDATION_LAUNCH_INTERVAL_MS_OPTIONS = [
  0,
  100,
  250,
  500,
  1000,
] as const;

function clampInt(value: number, min: number, max: number): number {
  const intValue = Number.isFinite(value) ? Math.floor(value) : min;
  return Math.min(max, Math.max(min, intValue));
}

export function getValidationMaxConcurrent(): number {
  if (typeof window === 'undefined') return VALIDATION_MAX_CONCURRENT_DEFAULT;

  try {
    const raw = window.localStorage.getItem(MAX_CONCURRENT_STORAGE_KEY);
    if (!raw) return VALIDATION_MAX_CONCURRENT_DEFAULT;

    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return VALIDATION_MAX_CONCURRENT_DEFAULT;

    return clampInt(
      parsed,
      VALIDATION_MAX_CONCURRENT_MIN,
      VALIDATION_MAX_CONCURRENT_MAX
    );
  } catch {
    return VALIDATION_MAX_CONCURRENT_DEFAULT;
  }
}

export function setValidationMaxConcurrent(value: number): void {
  if (typeof window === 'undefined') return;

  try {
    const clamped = clampInt(
      value,
      VALIDATION_MAX_CONCURRENT_MIN,
      VALIDATION_MAX_CONCURRENT_MAX
    );
    window.localStorage.setItem(MAX_CONCURRENT_STORAGE_KEY, String(clamped));
  } catch {
    // Best-effort: ignore storage failures.
  }
}

export function getValidationBatchSize(): number {
  if (typeof window === 'undefined') return VALIDATION_BATCH_SIZE_DEFAULT;

  try {
    const raw = window.localStorage.getItem(BATCH_SIZE_STORAGE_KEY);
    if (!raw) return VALIDATION_BATCH_SIZE_DEFAULT;

    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return VALIDATION_BATCH_SIZE_DEFAULT;

    // Only allow known options.
    if ((VALIDATION_BATCH_SIZE_OPTIONS as readonly number[]).includes(parsed)) {
      return parsed;
    }
    return VALIDATION_BATCH_SIZE_DEFAULT;
  } catch {
    return VALIDATION_BATCH_SIZE_DEFAULT;
  }
}

export function setValidationBatchSize(value: number): void {
  if (typeof window === 'undefined') return;

  try {
    const next = (VALIDATION_BATCH_SIZE_OPTIONS as readonly number[]).includes(
      value
    )
      ? value
      : VALIDATION_BATCH_SIZE_DEFAULT;
    window.localStorage.setItem(BATCH_SIZE_STORAGE_KEY, String(next));
  } catch {
    // Best-effort: ignore storage failures.
  }
}

export function getValidationLaunchIntervalMs(): number {
  if (typeof window === 'undefined') return VALIDATION_LAUNCH_INTERVAL_MS_DEFAULT;

  try {
    const raw = window.localStorage.getItem(LAUNCH_INTERVAL_MS_STORAGE_KEY);
    if (!raw) return VALIDATION_LAUNCH_INTERVAL_MS_DEFAULT;

    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return VALIDATION_LAUNCH_INTERVAL_MS_DEFAULT;

    if (
      (VALIDATION_LAUNCH_INTERVAL_MS_OPTIONS as readonly number[]).includes(parsed)
    ) {
      return parsed;
    }
    return VALIDATION_LAUNCH_INTERVAL_MS_DEFAULT;
  } catch {
    return VALIDATION_LAUNCH_INTERVAL_MS_DEFAULT;
  }
}

export function setValidationLaunchIntervalMs(value: number): void {
  if (typeof window === 'undefined') return;

  try {
    const next = (VALIDATION_LAUNCH_INTERVAL_MS_OPTIONS as readonly number[]).includes(
      value
    )
      ? value
      : VALIDATION_LAUNCH_INTERVAL_MS_DEFAULT;
    window.localStorage.setItem(LAUNCH_INTERVAL_MS_STORAGE_KEY, String(next));
  } catch {
    // Best-effort: ignore storage failures.
  }
}

